import { Deployments, User } from '../types';

export type MaybeUser = User | null | undefined;

export interface SignUpAvailabilityResult {
    canSignUp: boolean;
    errorMessage?: { shortReason: string; longReason: string };
}

type SignUpDeployment = {
    can_sign_up?: boolean;
    short_reasoning?: string | null;
    long_reasoning?: string | null;
};

const SIGN_UP_UNAVAILABLE = {
    shortReason: 'SIGN UP UNAVAILABLE',
    longReason:
        "New members can't be added right now. Please reach out to support@samaritan.city for any questions."
};

export const GENERIC_SIGN_UP_UNAVAILABLE = {
    shortReason: 'SIGN UP UNAVAILABLE',
    longReason:
        "New members can't be added right now. Either all seats are being used or the sign up window has ended. Please reach out to support@samaritan.city for any questions."
};

export const shouldHideIntakeDeployment = (deployment: {
    is_grandfathered?: boolean;
    short_reasoning?: string | null;
}) => {
    if (deployment.is_grandfathered) return true;
    return (deployment.short_reasoning ?? '').toLowerCase().includes('commitment');
};

const uniqueReasonings = (reasons: Array<string | null | undefined>) => {
    return Array.from(
        new Set(reasons.filter((reason): reason is string => Boolean(reason)))
    );
};

export const signUpAvailabilityFromDeployment = (
    deployment: SignUpDeployment | null | undefined
): SignUpAvailabilityResult => {
    if (deployment?.can_sign_up) {
        return { canSignUp: true };
    }

    if (!deployment?.short_reasoning && !deployment?.long_reasoning) {
        return { canSignUp: false, errorMessage: SIGN_UP_UNAVAILABLE };
    }

    return {
        canSignUp: false,
        errorMessage: {
            shortReason: deployment.short_reasoning || '',
            longReason: deployment.long_reasoning || ''
        }
    };
};

// Sign-up buttons (member list, side nav, org homepage).
// Enabled when any listed deployment has can_sign_up. A single shared long_reasoning is shown as-is.
// Different long_reasonings fall back to the generic message.
export const canUserSignUp = (user: MaybeUser, deployments: SignUpDeployment[]): SignUpAvailabilityResult => {
    if (!user || typeof user.id !== 'number') {
        return { canSignUp: false, errorMessage: SIGN_UP_UNAVAILABLE };
    }

    if (user.is_admin) return { canSignUp: true };

    if (!user.is_non_profit) {
        return { canSignUp: false, errorMessage: SIGN_UP_UNAVAILABLE };
    }

    if (deployments.some((deployment) => deployment.can_sign_up)) {
        return { canSignUp: true };
    }

    const shortReasons = uniqueReasonings(
        deployments.map((deployment) => deployment.short_reasoning)
    );
    const longReasons = uniqueReasonings(
        deployments.map((deployment) => deployment.long_reasoning)
    );

    if (longReasons.length !== 1) {
        return { canSignUp: false, errorMessage: GENERIC_SIGN_UP_UNAVAILABLE };
    }

    return {
        canSignUp: false,
        errorMessage: {
            shortReason: shortReasons.length === 1 ? shortReasons[0] : GENERIC_SIGN_UP_UNAVAILABLE.shortReason,
            longReason: longReasons.length === 1 ? longReasons[0] : GENERIC_SIGN_UP_UNAVAILABLE.longReason
        }
    };
};

export const canSignUpForDeployment = (
    deployment: Deployments
): SignUpAvailabilityResult => {
    console.log('canSignUpForDeployment', deployment);
    const result = signUpAvailabilityFromDeployment(deployment);
    console.log('result', result);
    return result;
};
